import { z } from 'zod';

/** How the edge resolves peripheral identity before publishing scan telemetry. */
export const PeripheralIdentityModeSchema = z.enum(['edge-guard', 'cloud-resolve']);
export type PeripheralIdentityMode = z.infer<typeof PeripheralIdentityModeSchema>;

export const PeripheralProfileIdentitySchema = z.object({
  mode: PeripheralIdentityModeSchema.default('edge-guard'),
  /** Host read protocol when mode is edge-guard (default rakinda-binary). */
  readProtocol: z.enum(['rakinda-binary']).optional(),
});
export type PeripheralProfileIdentity = z.infer<typeof PeripheralProfileIdentitySchema>;

export interface PeripheralUsbProfileRefInput {
  usbVendorId: string;
  usbProductId: string;
}

/** Normalize USB id for stable comparison (strip 0x, lowercase). */
export function normalizePeripheralVidPid(value: string): string {
  return value.replace(/^0x/i, '').toLowerCase();
}

/**
 * Canonical profile reference: `{deviceCategory}|{vid}:{pid}`.
 * Used by edge telemetry and cloud identity resolution (stopgap for devices without host read-S/N).
 */
export function buildPeripheralProfileRef(
  deviceCategory: string,
  profile: PeripheralUsbProfileRefInput,
): string {
  const vid = normalizePeripheralVidPid(profile.usbVendorId);
  const pid = normalizePeripheralVidPid(profile.usbProductId);
  return `${deviceCategory}|${vid}:${pid}`;
}

export function resolveProfileIdentityMode(profile: {
  identity?: { mode?: PeripheralIdentityMode };
}): PeripheralIdentityMode {
  return profile.identity?.mode ?? 'edge-guard';
}

/** `cnapse/edge/{tenantId}/{orchestratorType}/telemetry/...` — not gateway serial; use payload.gatewaySerialNumber for EFEM. */
export function parseGatewaySerialFromEdgeTopic(topic: string): string | null {
  const parts = topic.split('/');
  if (parts[0] === 'cnapse' && parts[1] === 'edge' && parts[2] && parts[3]) {
    try {
      return decodeURIComponent(parts[3]);
    } catch {
      return parts[3];
    }
  }
  return null;
}
